/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {MoneyHelper} from '../../../components/util/money';

export function getAvailableTeamTypes(LocalNetworkProfile: any): string[] {
  const isSuiPlatform = LocalNetworkProfile.isSuiPlatform();
  const isAdvertisingPlatform = LocalNetworkProfile.isAdvertisingPlatform();
  const isMarketplacePlatform = LocalNetworkProfile.isMarketplacePlatform();

  let teamTypes: string[] = [];
  if (isSuiPlatform) {
    teamTypes = ['STANDARD', 'SUI', 'AX_ADVERTISER', 'AX_PUBLISHER'];
  } else if (isAdvertisingPlatform) {
    teamTypes = ['STANDARD'];
    if (isMarketplacePlatform) {
      teamTypes.push('AX_ADVERTISER');
      teamTypes.push('AX_PUBLISHER');
      teamTypes.push('CREATIVE_AGENCY');
    }
  }

  if (LocalNetworkProfile.get('politicalApp')) {
    teamTypes = _.uniq(teamTypes.concat(['CONTROLLER', 'SPONSOR']));
  }

  return teamTypes;
}

export function initFeesForParam(ref: any, networkCurrencies: string[]): void {
  const currencies = _.uniq(networkCurrencies || []);
  const existing = _.isArray(ref.multiCurrencyMonetaryAmounts) ? ref.multiCurrencyMonetaryAmounts : [];
  const byCurrency: {[currency: string]: any} = {};

  // One row per currency. Prefer amounts that already have a value — _.merge after
  // save can otherwise leave duplicate currency rows in multiCurrencyMonetaryAmounts.
  _.forEach(existing, (fee) => {
    if (!fee || !_.isString(fee.currency)) {
      return;
    }
    const prev = byCurrency[fee.currency];
    if (!prev || (MoneyHelper.isMoney(fee) && !MoneyHelper.isMoney(prev))) {
      byCurrency[fee.currency] = fee;
    }
  });

  const monetary = _.get(ref, 'monetary');
  if (monetary && _.isString(monetary.currency) && !byCurrency[monetary.currency]) {
    ref.type = 'MULTI_CURRENCY_MONETARY';
    byCurrency[monetary.currency] = monetary;
  }

  ref.multiCurrencyMonetaryAmounts = _.map(currencies, (currency) => {
    return byCurrency[currency] || {currency: currency};
  });
}

function sanitiseMultiCurrencyAmounts(amounts: any): any[] {
  return _.filter(_.map(amounts || [], function(m) {
    return MoneyHelper.fixMoney(m);
  }), function(fee) {
    return MoneyHelper.isMoney(fee);
  });
}

function sanitiseFeeValue(feeClone: any): any {
  if (!feeClone || !feeClone.type) {
    return null;
  }
  if (feeClone.type === 'MONETARY') {
    const fixedFee = MoneyHelper.fixMoney(feeClone.monetary);
    if (MoneyHelper.isMoney(fixedFee)) {
      return {type: 'MONETARY', monetary: fixedFee};
    }
    return null;
  }
  if (feeClone.type === 'PERCENTAGE') {
    if (_.isFinite(_.get(feeClone, ['percentage']))) {
      return {type: 'PERCENTAGE', percentage: feeClone.percentage};
    }
    return null;
  }
  if (_.isArray(feeClone.multiCurrencyMonetaryAmounts)) {
    const mFees = sanitiseMultiCurrencyAmounts(feeClone.multiCurrencyMonetaryAmounts);
    if (mFees.length > 0) {
      return {type: 'MULTI_CURRENCY_MONETARY', multiCurrencyMonetaryAmounts: mFees};
    }
  }
  return null;
}

function layoutFeesFromUiOrApi(model: any): any[] {
  // Edit form uses uiLayoutFees; copy/API models only have layoutFees. Prefer UI when
  // present so empty form rows can clear fees, otherwise keep API fees intact.
  const source = _.isArray(model.uiLayoutFees) ? model.uiLayoutFees : model.layoutFees;
  return _.map(_.filter(source, function(layoutFee) {
    return _.get(layoutFee, ['layout', 'id']) && sanitiseMultiCurrencyAmounts(_.get(layoutFee, ['fee', 'multiCurrencyMonetaryAmounts'])).length > 0;
  }), function(layoutFee) {
    const adjFee = _.cloneDeep(layoutFee);
    adjFee.fee = adjFee.fee || {};
    adjFee.fee.type = adjFee.fee.type || 'MULTI_CURRENCY_MONETARY';
    adjFee.fee.multiCurrencyMonetaryAmounts = sanitiseMultiCurrencyAmounts(adjFee.fee.multiCurrencyMonetaryAmounts);
    return adjFee;
  });
}

function targetingTypeTechFeesFromUiOrApi(model: any): {[apiType: string]: any} {
  const result: {[apiType: string]: any} = {};
  if (_.isArray(model.uiTargetingTypeTechFees)) {
    _.forEach(model.uiTargetingTypeTechFees, function(ttFee) {
      if (!ttFee || !ttFee.apiType || !ttFee.fee) {
        return;
      }
      const fee = sanitiseFeeValue(ttFee.fee);
      if (fee && (fee.type !== 'PERCENTAGE' || fee.percentage > 0)) {
        result[ttFee.apiType] = fee;
      }
    });
    return result;
  }

  // API/copy path: targetingTypeTechFees is already a map keyed by targeting type.
  _.forEach(model.targetingTypeTechFees || {}, function(fee, apiType) {
    const sanitised = sanitiseFeeValue(fee);
    if (sanitised && (sanitised.type !== 'PERCENTAGE' || sanitised.percentage > 0)) {
      result[apiType] = sanitised;
    }
  });
  return result;
}

export function preTeamSaveFix(ctrl: {model: any}): void {
  function sanitiseFee(param: string): void {
    ctrl.model[param] = sanitiseFeeValue(_.cloneDeep(ctrl.model[param]));
  }

  if (!ctrl.model.type) {
    ctrl.model.type = 'STANDARD';
  }
  sanitiseFee('techFeeCpm');
  sanitiseFee('creativeTagTechFeeCpm');

  ctrl.model.supplementaryFees = _.filter(ctrl.model.supplementaryFees, function(fee) {
    if (!fee.name || !fee.type) {
      return false;
    }
    if (fee.type === 'PERCENTAGE' && _.isFinite(fee.percentage)) {
      delete fee.multiCurrencyMonetaryAmounts;
      return true;
    }
    const fixedFee = MoneyHelper.fixMoney(fee.monetary);
    if (fee.type === 'MONETARY' && MoneyHelper.isMoney(fixedFee)) {
      fee.monetary = fixedFee;
      return true;
    }
    if (fee.type === 'MULTI_CURRENCY_MONETARY' && _.get(fee, ['multiCurrencyMonetaryAmounts'], []).length > 0) {
      delete fee.percentage;
      fee.multiCurrencyMonetaryAmounts = sanitiseMultiCurrencyAmounts(fee.multiCurrencyMonetaryAmounts);
      return fee.multiCurrencyMonetaryAmounts.length > 0;
    }
    return false;
  });
  ctrl.model.layoutFees = layoutFeesFromUiOrApi(ctrl.model);
  ctrl.model.targetingTypeTechFees = targetingTypeTechFeesFromUiOrApi(ctrl.model);
}
